import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { apiService } from '../services/api';
import { TimeEntriesList } from './TimeEntriesList';
import { ProjectsManager } from './ProjectsManager';
import { ManageSettings } from './ManageSettings';
import { ModernNavigation } from './ModernNavigation';
import { ModernTimesheet } from './ModernTimesheet';
import TeamManager from './TeamManager';
import AISettings from './AISettings';
import ScreenshotHistory from './ScreenshotHistory';
import AIAnalytics from './AIAnalytics';
import Reports from './Reports';
import Expenses from './Expenses';
import Invoices from './Invoices';
import Estimates from './Estimates';
import { ProfileSettings } from './ProfileSettings';
import { AccountSettings } from './AccountSettings';
import { PersonalizationSettings } from './PersonalizationSettings';
import { AdminSettings } from './AdminSettings';
import { SuperAdminDashboard } from './SuperAdminDashboard';
import { DashboardOverview } from './DashboardOverview';
import { Testing } from './Testing';
import { OnboardingModal } from './OnboardingModal';

export const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ai-analytics');

  useEffect(() => {
    loadDashboardStats();
    
    // Listen for navigation events from AITrackingWidget
    const handleNavigateToTab = (event: CustomEvent) => {
      setActiveTab(event.detail);
    };
    
    window.addEventListener('navigate-to-tab', handleNavigateToTab as EventListener);
    
    return () => {
      window.removeEventListener('navigate-to-tab', handleNavigateToTab as EventListener);
    };
  }, []);

  const loadDashboardStats = async () => {
    try {
      await apiService.getDashboardStats();
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ModernNavigation 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
      />

      {/* Dashboard Overview Tab */}
      {activeTab === 'dashboard' && <DashboardOverview onNavigate={setActiveTab} />}

      {/* Time Tracking Tab */}
      {activeTab === 'time' && <ModernTimesheet />}
      
      {/* Projects Tab */}
      {activeTab === 'projects' && <ProjectsManager />}

      {/* Time Entries Tab */}
      {activeTab === 'entries' && (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <TimeEntriesList />
        </Container>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && <Expenses />}

      {/* Team Tab */}
      {activeTab === 'team' && <TeamManager />}


      {/* AI Settings Tab */}
      {activeTab === 'ai-settings' && <AISettings />}

      {/* Screenshot History Tab */}
      {activeTab === 'screenshot-history' && <ScreenshotHistory />}

      {/* AI Analytics Tab */}
      {activeTab === 'ai-analytics' && <AIAnalytics />}

      {/* Reports Tab */}
      {activeTab === 'reports' && <Reports />}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && <Invoices />}

      {/* Estimates Tab */}
      {activeTab === 'estimates' && <Estimates />}

      {/* Manage Tab */}
      {activeTab === 'manage' && <ManageSettings />}

      {/* Profile Settings Tab */}
      {activeTab === 'profile-settings' && <ProfileSettings />}

      {/* Account Settings Tab */}
      {activeTab === 'account-settings' && <AccountSettings />}

      {/* Personalization Settings Tab */}
      {activeTab === 'personalization-settings' && <PersonalizationSettings />}

      {/* Admin Settings Tab */}
      {activeTab === 'admin-settings' && <AdminSettings />}

      {/* Superadmin Dashboard Tab */}
      {activeTab === 'superadmin' && <SuperAdminDashboard />}

      {/* Testing Tab */}
      {activeTab === 'testing' && <Testing />}

      {/* Onboarding Guide for new users */}
      <OnboardingModal />
    </Box>
  );
};
